const EXPECTED_VALIDATION_400_PATHS = new Set([
  "/api/perform-accreditation-status-request",
]);

const EXPECTED_AUTH_401_PATHS = new Set([
  "/api/get-user-homes",
  "/api/me",
  "/api/notifications",
]);

const LOW_PRIORITY_NETWORK_PATHS = new Set([
  "/api/homes/get-messages",
]);

export function normalizeApiPath(url, baseURL = "") {
  const rawUrl = String(url || "");
  const rawBase = String(baseURL || "");

  if (!rawUrl) {
    return "/unknown";
  }

  let path;
  try {
    const fallbackOrigin = typeof window !== "undefined" && window.location
      ? window.location.origin
      : "https://invown.local";
    const parsed = new URL(rawUrl, rawBase || fallbackOrigin);
    path = parsed.pathname;
  } catch (_error) {
    path = rawUrl.split(/[?#]/)[0];
  }

  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  return path
    .replace(/\/+/g, "/")
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|$)/gi, "/:uuid")
    .replace(/\/\d+(?=\/|$)/g, "/:id")
    .replace(/\/$/, "") || "/";
}

function methodFromConfig(config = {}) {
  return String(config.method || "GET").toUpperCase();
}

function statusFromResponse(response) {
  const status = response?.status;
  return status === undefined || status === null ? "unknown" : String(status);
}

export function classifyApiRequest(path) {
  if (path.startsWith("/api/admin")) {
    return {
      workflow: path.includes("accreditation") ? "accreditation" : "admin",
      requestCategory: "admin",
    };
  }
  if (path.includes("accreditation")) {
    return {
      workflow: "accreditation",
      requestCategory: "validation",
    };
  }
  if (path.startsWith("/api/checkout") || [
    "/api/to-cart",
    "/api/submit-cart",
    "/api/update-cart-amount",
  ].includes(path)) {
    return {
      workflow: "checkout",
      requestCategory: "transactional",
    };
  }
  if (path.startsWith("/api/homes")) {
    return {
      workflow: "offering",
      requestCategory: "content",
    };
  }
  return {
    workflow: "api",
    requestCategory: "api",
  };
}

export function getAxiosErrorDetails(error) {
  if (!error || !error.isAxiosError) {
    return null;
  }

  const config = error.config || {};
  const path = normalizeApiPath(config.url, config.baseURL);
  const status = statusFromResponse(error.response);
  const method = methodFromConfig(config);
  const classification = classifyApiRequest(path);

  return {
    method,
    path,
    status,
    workflow: classification.workflow,
    requestCategory: classification.requestCategory,
    message: error.response?.data?.message || error.message,
  };
}

export function isExpectedAxiosValidationError(error) {
  const details = getAxiosErrorDetails(error);
  return Boolean(
    details &&
    details.status === "400" &&
    EXPECTED_VALIDATION_400_PATHS.has(details.path)
  );
}

export function isExpectedAxiosNoise(error) {
  const details = getAxiosErrorDetails(error);
  if (!details) {
    return false;
  }

  return details.status === "401" && EXPECTED_AUTH_401_PATHS.has(details.path);
}

export function isLowPriorityAxiosNetworkNoise(error) {
  const details = getAxiosErrorDetails(error);
  if (!details) {
    return false;
  }

  return Boolean(
    details.method === "GET" &&
    details.status === "unknown" &&
    LOW_PRIORITY_NETWORK_PATHS.has(details.path) &&
    /\b(Network Error|Failed to fetch|Load failed|The network connection was lost)\b/i
      .test(details.message || "")
  );
}

export function prepareAxiosSentryEvent(event, hint) {
  const error = hint?.originalException;
  const details = getAxiosErrorDetails(error);

  if (!details) {
    return event;
  }

  if (isExpectedAxiosValidationError(error)) {
    return null;
  }

  if (isExpectedAxiosNoise(error)) {
    return null;
  }

  const prepared = {
    ...event,
    tags: {
      ...(event.tags || {}),
      axios_method: details.method,
      axios_path: details.path,
      http_status: details.status,
      request_category: details.requestCategory,
      workflow: details.workflow,
    },
    contexts: {
      ...(event.contexts || {}),
      api_error: {
        method: details.method,
        path: details.path,
        status: details.status,
        workflow: details.workflow,
        request_category: details.requestCategory,
        message: details.message,
      },
    },
    fingerprint: [
      "axios",
      details.method,
      details.path,
      details.status,
    ],
  };

  if (isLowPriorityAxiosNetworkNoise(error)) {
    return {
      ...prepared,
      level: "warning",
      tags: {
        ...(prepared.tags || {}),
        low_priority_network_error: "true",
      },
      contexts: {
        ...(prepared.contexts || {}),
        api_error: {
          ...(prepared.contexts?.api_error || {}),
          low_priority_reason: "noncritical_background_get",
        },
      },
    };
  }

  return prepared;
}
